import { useEffect, useRef, useState, useCallback } from "react";
import api from "./api";

/**
 * useGeofenceWatcher — foreground geofence ping driver.
 *
 * Behaviour:
 *  - Only runs when `enabled` is true (user has flipped the global toggle).
 *  - Uses navigator.geolocation.watchPosition with high accuracy.
 *  - Sends a /api/geofence/ping at most every PING_INTERVAL_MS (default 15s)
 *    OR when the position has moved more than SIG_DISTANCE_M (default 30m).
 *  - On returned events, runs the supplied `onEvent` callback once per event.
 *  - Cleans up cleanly on disable / unmount.
 *
 * Future native wrapper: the same /api/geofence/ping endpoint can accept
 * `device_id` and `accuracy`, so an iOS/Android background app can plug in
 * without code changes.
 */

const PING_INTERVAL_MS = 15_000;     // every 15s while moving
const SIG_DISTANCE_M = 30;           // OR after ~30m of movement
const MIN_ACCURACY_M = 200;          // skip fixes worse than 200m

function haversine(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function useGeofenceWatcher({ enabled, onEvent }) {
  const [permission, setPermission] = useState(null); // "granted" | "denied" | "prompt"
  const watcherIdRef = useRef(null);
  const lastPingAtRef = useRef(0);
  const lastPingPosRef = useRef(null);
  const onEventRef = useRef(onEvent);

  // Keep the latest onEvent without re-installing the watcher
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const doPing = useCallback(async (coords) => {
    try {
      const { data } = await api.post("/geofence/ping", {
        lat: coords.latitude,
        lng: coords.longitude,
        accuracy: coords.accuracy ?? null,
      });
      if (Array.isArray(data) && data.length) {
        data.forEach((ev) => {
          try { onEventRef.current?.(ev); } catch {}
        });
      }
    } catch {
      // silent — geofence shouldn't disrupt the user
    }
  }, []);

  const onPosition = useCallback(
    (pos) => {
      const { latitude, longitude, accuracy } = pos.coords || {};
      if (latitude == null || longitude == null) return;
      if (accuracy && accuracy > MIN_ACCURACY_M) return;
      const now = Date.now();
      const here = { lat: latitude, lng: longitude };
      const moved = haversine(lastPingPosRef.current, here);
      if (
        now - lastPingAtRef.current >= PING_INTERVAL_MS ||
        moved >= SIG_DISTANCE_M
      ) {
        lastPingAtRef.current = now;
        lastPingPosRef.current = here;
        doPing(pos.coords);
      }
    },
    [doPing]
  );

  const onError = useCallback((err) => {
    if (err.code === err.PERMISSION_DENIED) setPermission("denied");
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (!navigator.geolocation) return;
    // Permissions probe (Chromium); silent on Safari
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((p) => setPermission(p.state))
        .catch(() => {});
    }
    const id = navigator.geolocation.watchPosition(onPosition, onError, {
      enableHighAccuracy: true,
      maximumAge: 10_000,
      timeout: 20_000,
    });
    watcherIdRef.current = id;
    return () => {
      if (watcherIdRef.current != null) {
        navigator.geolocation.clearWatch(watcherIdRef.current);
        watcherIdRef.current = null;
      }
    };
  }, [enabled, onPosition, onError]);

  return { permission };
}

/** Request Notification permission once. Returns "granted" | "denied" | "default". */
export async function ensureNotificationPermission() {
  if (typeof Notification === "undefined") return "denied";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    const res = await Notification.requestPermission();
    return res;
  } catch {
    return "denied";
  }
}

/** Show a browser notification (best-effort; falls back silently if denied). */
export function showSystemNotification(event) {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  const action = event.event === "arrival" ? "Arrived" : "Left";
  const place = event.location_name || event.location_address || "your saved place";
  try {
    new Notification(`${action} at ${place}`, {
      body: `Task: ${event.task_title}`,
      icon: "/icon-192.png",
      tag: `geofence-${event.task_id}-${event.event}`,
      requireInteraction: false,
    });
  } catch {
    // ignore
  }
}
